/**
 * Presentation layer for native Shopify policy pages.
 *
 * The section navigation is a small-screen affordance only. On desktop a 28-item contents
 * list made the policy read as technical documentation rather than a customer-care page, so
 * the sidebar is hidden there and the content runs as one wide editorial column; the markup
 * is still emitted so a narrow viewport (or a resize down) has it.
 *
 * /policies/* has no theme template — Shopify renders the markup itself — so this is built
 * from the rendered document rather than from Liquid. It is pure progressive enhancement:
 * the policy is server-rendered and fully usable with this script blocked, and nothing here
 * writes to the policy body beyond adding ids to headings so they can be linked.
 *
 * The four policies do not agree on which level marks a section: Shipping and Terms use H2
 * throughout, Privacy mixes H2 sections with H3 sub-sections, and Refund has a single H2
 * title with 24 H3 sections. So the level is chosen per document rather than assumed.
 */

const DESKTOP = '(min-width: 1000px)';
const MIN_SECTIONS = 3;

/** Wrappers this module creates, which act as boundaries once they exist. */
const COMPONENTS =
  '.policy-callout, .policy-action, .policy-exclusion, .policy-support, .policy-journey, .policy-journey__eyebrow';

/** @param {string} text */
function normalise(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** @param {string} text */
function slugify(text) {
  return (
    text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .slice(0, 60) || 'section'
  );
}

/**
 * The heading level that marks sections in this particular document.
 *
 * Prefers H2, but falls back to H3 when a policy uses a lone H2 as its title — building the
 * nav from H2 there would produce a single useless entry.
 *
 * @param {Element} body
 * @returns {HTMLHeadingElement[]}
 */
function sectionHeadings(body) {
  const h2 = /** @type {HTMLHeadingElement[]} */ ([...body.querySelectorAll('h2')]);
  if (h2.length >= MIN_SECTIONS) return h2;

  const h3 = /** @type {HTMLHeadingElement[]} */ ([...body.querySelectorAll('h3')]);
  return h3.length >= MIN_SECTIONS ? h3 : [];
}

/** @param {HTMLHeadingElement[]} headings */
function assignIds(headings) {
  const used = new Set();
  for (const heading of headings) {
    if (heading.id) {
      used.add(heading.id);
      continue;
    }
    let id = slugify(heading.textContent ?? '');
    let n = 2;
    while (used.has(id)) id = `${slugify(heading.textContent ?? '')}-${n++}`;
    used.add(id);
    heading.id = id;
  }
}

/**
 * @param {HTMLHeadingElement[]} headings
 * @returns {HTMLElement}
 */
function buildNav(headings) {
  const details = document.createElement('details');
  details.className = 'policy-toc';

  const summary = document.createElement('summary');
  summary.className = 'policy-toc__summary';
  summary.textContent = 'Jump to section';
  details.append(summary);

  const list = document.createElement('ul');
  list.className = 'policy-toc__list';

  for (const heading of headings) {
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.className = 'policy-toc__link';
    link.href = `#${heading.id}`;
    link.textContent = (heading.textContent ?? '').trim();
    item.append(link);
    list.append(item);
  }

  details.append(list);
  return details;
}

/**
 * Marks the section currently being read.
 *
 * The rootMargin pins the "current" line near the top of the viewport, so a heading counts
 * as active once it reaches reading position rather than the moment it appears at the
 * bottom. Falls back silently where IntersectionObserver is unavailable — the nav still
 * links correctly, it just does not highlight.
 *
 * @param {HTMLHeadingElement[]} headings
 * @param {HTMLElement} nav
 */
function trackActive(headings, nav) {
  if (!('IntersectionObserver' in window)) return;

  const links = new Map(
    [...nav.querySelectorAll('.policy-toc__link')].map((link) => [
      (link.getAttribute('href') ?? '').slice(1),
      link,
    ])
  );
  const visible = new Set();

  const setActive = () => {
    // The topmost visible heading wins; if none is on screen, keep the last one passed.
    let current = null;
    for (const heading of headings) {
      if (visible.has(heading.id)) {
        current = heading.id;
        break;
      }
      if (heading.getBoundingClientRect().top < 0) current = heading.id;
    }
    for (const [id, link] of links) {
      const active = id === current;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.add(entry.target.id);
        else visible.delete(entry.target.id);
      }
      setActive();
    },
    { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
  );

  for (const heading of headings) observer.observe(heading);

  // Programmatic scrolling and occluded tabs can leave the observer quiet, so the scroll
  // container drives a recheck too. Cheap: one rect read per visible heading.
  const scroller = document.querySelector('.page-wrapper') ?? document;
  scroller.addEventListener('scroll', setActive, { passive: true });
  setActive();
}

/* ------------------------------------------------------------------------------
   Presentation layer.

   Everything below adds presentation around the policy — a supporting line, an operational
   summary, a callout and a support block. It never edits, rewrites or removes a sentence of
   the legal text: the policy is server-rendered and complete without this script, which is
   what keeps it crawlable and readable with JS blocked.

   The Shipping summary figures are transcribed from the policy body itself (free shipping on
   eligible contiguous-US orders, 1–2 days processing, 3–9 days transit) so the strip cannot
   state something the policy does not.
   ------------------------------------------------------------------------------ */

/** Which policy this is, from the title Shopify renders. */
function policyKind() {
  const title = normalise(document.querySelector('.shopify-policy__title h1')?.textContent ?? '');
  if (title.includes('shipping')) return 'shipping';
  if (title.includes('warranty')) return 'warranty';
  if (title.includes('refund') || title.includes('return')) return 'refund';
  if (title.includes('privacy')) return 'privacy';
  if (title.includes('terms')) return 'terms';
  return 'other';
}

/**
 * Collects a heading and everything up to the next heading of the same level or higher.
 *
 * @param {Element} heading
 * @returns {Element[]}
 */
function sectionNodes(heading) {
  const level = Number(heading.tagName.slice(1));
  const nodes = [heading];
  let node = heading.nextElementSibling;
  while (node) {
    // A section already wrapped as a component ends this one, even though its heading is no
    // longer a sibling. Without this, wrapping an earlier section afterwards absorbs the
    // later component whole and it inherits the wrong measure.
    if (node.matches?.(COMPONENTS)) break;
    const match = /^H([1-6])$/.exec(node.tagName);
    if (match && Number(match[1]) <= level) break;
    nodes.push(node);
    node = node.nextElementSibling;
  }
  return nodes;
}

/**
 * @param {Element} body
 * @param {RegExp} pattern
 * @returns {Element | null}
 */
function findHeading(body, pattern) {
  return (
    [...body.querySelectorAll('h1, h2, h3')].find((h) => pattern.test(h.textContent ?? '')) ?? null
  );
}

/**
 * Marks the paragraphs a callout should set as figures.
 *
 * The real test is that the paragraph is nothing but one bold run, and CSS cannot express
 * it: `strong:only-child` counts element children only, so it matches a bold word sitting
 * mid-sentence between two text nodes just as readily and promotes it to a display line.
 * Comparing the paragraph's text to the run's is the check that actually holds.
 *
 * @param {Element} scope
 */
function markFigures(scope) {
  for (const paragraph of scope.querySelectorAll(':scope > p')) {
    const strong = paragraph.firstElementChild;
    if (paragraph.children.length !== 1 || strong?.tagName !== 'STRONG') continue;
    if ((paragraph.textContent ?? '').trim() !== (strong.textContent ?? '').trim()) continue;
    paragraph.classList.add('policy-figure');
  }
}

/**
 * @param {Element} heading
 * @returns {Element | null}
 */
function wrapCallout(heading) {
  const block = wrapNodes(sectionNodes(heading), 'policy-callout');
  if (block) markFigures(block);
  return block;
}

/** @param {Element[]} nodes @param {string} className */
function wrapNodes(nodes, className) {
  if (!nodes.length) return null;
  const wrapper = document.createElement('div');
  wrapper.className = className;
  nodes[0].before(wrapper);
  wrapper.append(...nodes);
  return wrapper;
}

/**
 * Drops the body's own restatement of the page title from view.
 *
 * It is a duplicate of Shopify's H1 and adds nothing; it stays in the document (hidden from
 * layout, not from assistive technology's document outline) rather than being deleted, so no
 * policy content is removed.
 *
 * @param {Element} body
 */
function hideDuplicateTitle(body) {
  const pageTitle = normalise(document.querySelector('.shopify-policy__title h1')?.textContent ?? '');
  const first = body.querySelector('h1, h2, h3');
  if (!first || !pageTitle) return;
  if (normalise(first.textContent ?? '').includes(pageTitle)) {
    first.classList.add('policy-duplicate-title');
  }
}

/**
 * The supporting line under the H1 and the operational summary.
 *
 * Shipping only: the figures are specific to that policy, and inventing an equivalent for
 * Refund or Terms would mean writing claims their bodies do not make.
 *
 * @param {Element} container
 * @param {Element} body
 */
function addHero(container, body, intro, facts) {
  const title = container.querySelector('.shopify-policy__title');
  if (!title || container.querySelector('.policy-intro')) return;

  const lead = document.createElement('p');
  lead.className = 'policy-intro';
  lead.textContent = intro;
  title.append(lead);

  const strip = document.createElement('ul');
  strip.className = 'policy-strip';
  // Redundant on paper, load-bearing in Safari: removing the markers to draw the strip also
  // removes the list role there, so VoiceOver stops announcing how many items there are.
  strip.setAttribute('role', 'list');
  for (const [fact, label] of facts) {
    const item = document.createElement('li');
    const value = document.createElement('strong');
    value.textContent = fact;
    const note = document.createElement('span');
    note.className = 'policy-strip__note';
    note.textContent = label;
    item.append(value, note);
    strip.append(item);
  }
  body.before(strip);
}

/**
 * Promotes two sections that carry operational weight.
 *
 * @param {Element} body
 */
function addShippingComponents(body) {
  const delivery = findHeading(body, /estimated delivery/i);
  if (delivery) wrapCallout(delivery);

  // Sections where the customer has something to do, rather than something to know.
  for (const pattern of [/shipping damage/i, /incorrect product received/i, /refused deliveries/i]) {
    const heading = findHeading(body, pattern);
    if (heading) wrapNodes(sectionNodes(heading), 'policy-action');
  }

  addSupport(body, /questions about your shipment/i);
}

/**
 * The Refund body is a flat run of peer topics, so the same components carry different
 * weight here: the callouts mark the two things that decide whether a refund happens at
 * all — the authorization rule and the exclusions — rather than a delivery estimate.
 *
 * @param {Element} body
 */
function addRefundComponents(body) {
  for (const pattern of [/before returning a product/i, /not eligible for standard return/i]) {
    const heading = findHeading(body, pattern);
    if (heading) wrapCallout(heading);
  }

  // Same rule as Shipping: the customer has something to do, not just something to know.
  for (const pattern of [
    /damaged on arrival/i,
    /incorrect product received/i,
    /^return shipping$/i,
    /refused deliveries/i,
  ]) {
    const heading = findHeading(body, pattern);
    if (heading) wrapNodes(sectionNodes(heading), 'policy-action');
  }

  addSupport(body, /questions about a return/i);
}

/**
 * The hero, strip and journey are Liquid on this page; only the body needs reaching into.
 *
 * The split the page turns on is coverage versus its limits, so the two treatments are
 * pulled apart: a filled teal panel for what the warranty gives, a neutral rule for what it
 * does not. Teal never marks an exclusion, so the two never read as the same thing.
 *
 * @param {Element} body
 */
function addWarrantyComponents(body) {
  for (const pattern of [
    /warranty period/i,
    /what is covered/i,
    /how to submit a warranty claim/i,
  ]) {
    const heading = findHeading(body, pattern);
    if (heading) wrapCallout(heading);
  }

  for (const pattern of [
    /what is not covered/i,
    /normal wear items/i,
    /battery warranty/i,
    /water .{0,3} moisture damage/i,
    /accidents .{0,3} impact damage/i,
    /unauthorized modifications/i,
    /commercial, rental/i,
  ]) {
    const heading = findHeading(body, pattern);
    if (heading) wrapNodes(sectionNodes(heading), 'policy-exclusion');
  }

  for (const pattern of [
    /shipping damage/i,
    /warranty shipping costs/i,
    /customer responsibilities/i,
  ]) {
    const heading = findHeading(body, pattern);
    if (heading) wrapNodes(sectionNodes(heading), 'policy-action');
  }

  addSupport(body, /contact kewelo/i);
}

/**
 * Privacy is a governance document, not a process, so it gets no journey and far fewer
 * panels than the others: four of twenty-one sections. The rest carries on typography and
 * whitespace, which is what a document read for reference rather than followed step by step
 * actually needs.
 *
 * @param {Element} body
 */
function addPrivacyComponents(body) {
  // The two sections a reader arrives wanting: what their choices are, and how to use them.
  for (const pattern of [/targeted advertising/i, /exercising your rights/i]) {
    const heading = findHeading(body, pattern);
    if (heading) wrapCallout(heading);
  }

  // Same rule as the other policies: the reader has something to do here.
  for (const pattern of [/managing communication preferences/i, /complaints/i]) {
    const heading = findHeading(body, pattern);
    if (heading) wrapNodes(sectionNodes(heading), 'policy-action');
  }

  markRevisionDate(body);
  addSupport(body, /contact us/i, /please contact/i);
}

/**
 * Sets the revision line as a dated masthead rather than a bold sentence.
 *
 * It opens the body, so at body weight it reads as the policy's first statement instead of
 * as metadata about it. The text is untouched.
 *
 * @param {Element} body
 */
function markRevisionDate(body) {
  const paragraph = [...body.querySelectorAll('p')].find((p) =>
    /^last updated/i.test((p.textContent ?? '').trim())
  );
  if (paragraph) paragraph.classList.add('policy-updated');
}

/**
 * Terms is the most formal document of the set and the quietest to treat.
 *
 * Two bands earn emphasis because they are what a rider actually needs to find: what the
 * customer must do with the product, and where KEWELO's obligations stop. Everything else
 * is a numbered contractual clause and reads as one.
 *
 * @param {Element} body
 */
function addTermsComponents(body) {
  // The clause that decides whether a product may lawfully be ridden where it is bought.
  const localLaw = findHeading(body, /local laws/i);
  if (localLaw) wrapCallout(localLaw);

  // Consecutive by design: assembly, riding, batteries — the customer's own obligations.
  for (const pattern of [/assembly/i, /safe product use/i, /lithium-ion battery safety/i]) {
    const heading = findHeading(body, pattern);
    if (heading) wrapNodes(sectionNodes(heading), 'policy-action');
  }

  // Where the agreement limits itself. Neutral, as on Warranty: teal never marks a limit.
  for (const pattern of [
    /disclaimer of warranties/i,
    /limitation of liability/i,
    /indemnification/i,
  ]) {
    const heading = findHeading(body, pattern);
    if (heading) wrapNodes(sectionNodes(heading), 'policy-exclusion');
  }

  // No primary action: the block opens "may be directed to", which is not an invitation to
  // act on, so the address stays a plain detail rather than being dressed as a control.
  addSupport(body, /contact information/i);
}

/**
 * Drops a body heading one level.
 *
 * The Warranty body opens with its own `<h1>`, so the page ships two. The text is the
 * clearest statement of the term the storefront advertises, so it stays visible and simply
 * stops competing with the page title.
 *
 * @param {Element} body
 */
function demoteFirstHeading(body) {
  const heading = body.querySelector('h1');
  if (!heading) return;

  const demoted = document.createElement('h2');
  for (const { name, value } of [...heading.attributes]) demoted.setAttribute(name, value);
  demoted.append(...heading.childNodes);
  heading.replaceWith(demoted);
}

/**
 * Rebuilds a flat run of H3s into a real outline.
 *
 * The Refund body sets every section as an H3 under a single H2, so the document claims
 * two dozen peer topics all sit inside one section. Promoting them to H2 makes the outline
 * match the content; the few that genuinely belong to the section above them stay H3.
 * Levels are changed by replacing the element, so the heading's text and any inline markup
 * carry over untouched.
 *
 * @param {Element} body
 * @param {RegExp[]} subsections headings that stay one level down
 */
function promoteHeadings(body, subsections) {
  for (const heading of [...body.querySelectorAll('h3')]) {
    const text = heading.textContent ?? '';
    if (subsections.some((pattern) => pattern.test(text))) continue;

    const promoted = document.createElement('h2');
    for (const { name, value } of [...heading.attributes]) promoted.setAttribute(name, value);
    promoted.append(...heading.childNodes);
    heading.replaceWith(promoted);
  }
}

/**
 * Turns the policies this one names into links a customer can follow.
 *
 * Only phrases already in the body are wrapped, and only where the destination exists —
 * a reference to a document the store has not published stays plain text rather than
 * becoming a broken promise. One link per target per section keeps the prose readable.
 *
 * @param {Element} body
 * @param {Array<{pattern: RegExp, href: string}>} refs
 */
function linkPolicyReferences(body, refs) {
  const sections = [...body.querySelectorAll('h2')].map((h) => sectionNodes(h));

  for (const nodes of sections) {
    for (const { pattern, href } of refs) {
      let linked = false;

      for (const node of nodes) {
        if (linked || /^H[1-6]$/.test(node.tagName)) continue;

        const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
        let text;
        while ((text = /** @type {Text} */ (walker.nextNode()))) {
          if (text.parentElement?.closest('a')) continue;
          const match = pattern.exec(text.textContent ?? '');
          if (!match) continue;

          const after = text.splitText(match.index);
          after.splitText(match[0].length);
          const link = document.createElement('a');
          link.className = 'policy-reference';
          link.href = href;
          link.textContent = match[0];
          after.replaceWith(link);
          linked = true;
          break;
        }
      }
    }
  }
}

/**
 * @param {Element} body
 * @param {RegExp} pattern heading that opens the support section
 * @param {RegExp} [action] phrase in the block's opening sentence to make the one action
 */
function addSupport(body, pattern, action) {
  const support = findHeading(body, pattern);
  if (support) {
    const block = wrapNodes(sectionNodes(support), 'policy-support');
    if (block) {
      linkifyContacts(block);
      const address = block
        .querySelector('a[href^="mailto:"]')
        ?.getAttribute('href')
        ?.slice('mailto:'.length);
      if (address) linkPrimaryAction(block, address, action);
    }
  }
}

/**
 * A compact view of what happens between order and delivery.
 *
 * Every step and every figure is already stated in the policy: processing takes 1–2 business
 * days, shipping confirmation and tracking follow dispatch, transit runs 3–9 business days.
 * Nothing here is a new commitment — it restates the sequence the prose already describes so
 * a customer can grasp it without reading four sections.
 *
 * @param {Element} body
 */
function addJourney(body, anchorPattern, label, eyebrowText, steps) {
  if (body.querySelector('.policy-journey')) return;

  const anchor = findHeading(body, anchorPattern);
  if (!anchor) return;

  const list = document.createElement('ol');
  list.className = 'policy-journey';
  list.setAttribute('aria-label', label);

  steps.forEach(([name, detail], index) => {
    const item = document.createElement('li');
    // Decorative: the ordered list already conveys sequence to assistive technology.
    const number = document.createElement('span');
    number.className = 'policy-journey__number';
    number.setAttribute('aria-hidden', 'true');
    number.textContent = String(index + 1).padStart(2, '0');
    const title = document.createElement('span');
    title.className = 'policy-journey__step';
    title.textContent = name;
    item.append(number, title);
    if (detail) {
      const note = document.createElement('span');
      note.className = 'policy-journey__detail';
      note.textContent = detail;
      item.append(note);
    }
    list.append(item);
  });

  const eyebrow = document.createElement('p');
  eyebrow.className = 'policy-journey__eyebrow';
  eyebrow.textContent = eyebrowText;

  // The anchor section may already be wrapped in a component, and the journey belongs at the
  // body's own measure rather than inside a panel.
  const target = anchor.closest('.policy-callout, .policy-action, .policy-support') ?? anchor;
  target.before(eyebrow, list);
}

/**
 * Makes the support block's contact details actionable without altering what they say.
 *
 * Only the existing email and phone strings are wrapped — no text is added, removed or
 * reworded, and the link text is exactly what was already on the page.
 *
 * @param {Element} scope
 */
function linkifyContacts(scope) {
  const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
  /** @type {Text[]} */
  const nodes = [];
  let node;
  while ((node = walker.nextNode())) nodes.push(/** @type {Text} */ (node));

  const pattern = /([\w.+-]+@[\w-]+\.[\w.]+)|(\+?\d[\d\s()\-]{8,}\d)/;

  // The pasted body wraps the support address in an anchor with no href — it looks like a
  // link and does nothing. Give those a destination rather than leaving a dead control.
  for (const anchor of scope.querySelectorAll('a:not([href]), a[href=""]')) {
    const value = (anchor.textContent ?? '').trim();
    const found = pattern.exec(value);
    if (!found || found[0] !== value) continue;
    anchor.setAttribute(
      'href',
      found[1] ? `mailto:${value}` : `tel:${value.replace(/[^+\d]/g, '')}`
    );
  }

  for (const text of nodes) {
    if (text.parentElement?.closest('a')) continue;
    const match = pattern.exec(text.textContent ?? '');
    if (!match) continue;

    const value = match[0];
    const isEmail = Boolean(match[1]);
    const after = text.splitText(match.index);
    after.splitText(value.length);

    const link = document.createElement('a');
    link.href = isEmail ? `mailto:${value}` : `tel:${value.replace(/[^+\d]/g, '')}`;
    link.textContent = value;
    after.replaceWith(link);
  }
}

/**
 * Turns the sentence the block already ends on into the way to act on it.
 *
 * The phrase is wrapped where it stands: no text is added, removed or reordered, and the
 * destination is the address the block lists a few lines below.
 *
 * @param {Element} scope
 * @param {string} address
 * @param {RegExp} [pattern] phrase to wrap; each policy words its own invitation to get in
 *   touch, so the phrase differs even though the treatment does not
 */
function linkPrimaryAction(scope, address, pattern) {
  if (scope.querySelector('.policy-support__action')) return;

  const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
  const phrase = pattern ?? /contact KEWELO Customer Support/i;
  let node;

  while ((node = walker.nextNode())) {
    const text = /** @type {Text} */ (node);
    if (text.parentElement?.closest('a')) continue;
    const match = phrase.exec(text.textContent ?? '');
    if (!match) continue;

    const after = text.splitText(match.index);
    after.splitText(match[0].length);

    const link = document.createElement('a');
    link.className = 'policy-support__action';
    link.href = `mailto:${address}`;
    link.textContent = match[0];
    after.replaceWith(link);
    return;
  }
}

function init() {
  const container = document.querySelector('.shopify-policy__container');
  const body = container?.querySelector('.shopify-policy__body');
  if (!container || !body) return;

  const kind = policyKind();

  // Before the outline is read, so ids and contents are built from the corrected hierarchy.
  if (kind === 'refund') {
    promoteHeadings(body, [/return eligibility/i, /partial refunds/i]);
  }
  if (kind === 'warranty') {
    demoteFirstHeading(body);
  }

  const headings = sectionHeadings(body);
  if (!headings.length) return;

  // A policy body usually repeats its own title as the first heading ("KEWELO Shipping
  // Policy" under Shopify's "Shipping policy"). Listing it as the first contents entry is
  // noise, so it is skipped whenever it restates the page title. Matched on normalised text
  // rather than on any specific policy's wording.
  const pageTitle = normalise(
    document.querySelector('.shopify-policy__title h1')?.textContent ?? ''
  );
  if (headings.length > 1 && pageTitle && normalise(headings[0].textContent ?? '').includes(pageTitle)) {
    headings.shift();
  }

  assignIds(headings);

  hideDuplicateTitle(body);

  if (kind === 'shipping') {
    addHero(
      container,
      body,
      'Clear delivery expectations, U.S. fulfillment information, and everything you need to know about receiving your KEWELO order.',
      [
        // Coverage, not timing. The figures live in the order journey; repeating them here
        // would state the same commitment twice.
        ['Free shipping', 'On eligible orders'],
        ['U.S. fulfillment', 'U.S.-based inventory'],
        ['Tracked delivery', 'Tracking after shipment'],
        ['Customer support', 'Help throughout delivery'],
      ]
    );
    addShippingComponents(body);
    addJourney(body, /order processing/i, 'Order journey', 'From order to your door', [
      // Only the two figures the policy states are timings; the rest describe what happens
      // at that step, so no step implies a commitment the body has not already made.
      ['Order placed', 'Confirmation sent'],
      ['Processing', '1–2 business days'],
      ['Shipped', 'Order dispatched'],
      ['Tracking', 'Tracking sent by email'],
      ['Delivery', '3–9 business days in transit'],
    ]);
  }

  if (kind === 'refund') {
    addHero(
      container,
      body,
      'When a KEWELO product can be returned, how to request a return, who is responsible for return shipping, and how approved refunds are processed.',
      [
        // Terms, not process — the sequence is the journey's job. Each value is a condition
        // the policy states outright, so the strip commits to nothing extra.
        ['Return window', '30 calendar days from delivery'],
        ['Condition', 'New or like-new, complete'],
        ['Return shipping', 'Customer-paid on change of mind'],
        ['Refund method', 'Normally the original payment'],
      ]
    );
    addRefundComponents(body);
    addJourney(body, /before returning a product/i, 'Return process', 'How a return works', [
      // Nothing here promises approval: review and inspection are stated as assessments, and
      // the only figure is the one the Refunds section gives for refunds already approved.
      ['Request', 'Contact support first'],
      ['Review', 'Eligibility assessed'],
      ['Authorization', 'Return instructions provided'],
      ['Inspection', 'Returned condition assessed'],
      ['Refund', '5–10 business days once approved'],
    ]);
    linkPolicyReferences(body, [
      // The body also names a Cancellation Policy, which the store has not published — that
      // reference stays plain text rather than pointing at a 404.
      { pattern: /KEWELO Limited Warranty Policy|KEWELO Limited Warranty/, href: '/pages/warranty-policy' },
      { pattern: /Terms of Service/, href: '/policies/terms-of-service' },
    ]);
  }

  if (kind === 'privacy') {
    addHero(
      container,
      body,
      'How KEWELO collects, uses, discloses, and protects personal information when you shop with us, contact support, or otherwise interact with our services.',
      [
        // The document's own spine — sections 2, 4, 7 and 14. Each value names what that
        // section is about; none of them states a commitment the policy does not make.
        ['Information', 'What we collect'],
        ['Use', 'How it is used'],
        ['Disclosure', 'Who it is shared with'],
        ['Your rights', 'Vary by where you live'],
      ]
    );
    addPrivacyComponents(body);
    linkPolicyReferences(body, [
      { pattern: /Terms of Service/, href: '/policies/terms-of-service' },
    ]);
  }

  if (kind === 'terms') {
    addHero(
      container,
      body,
      'The terms that apply when you use KEWELO.com, place an order, or purchase a product from us.',
      [
        // Scope, not obligations: each pair names a run of clauses the document already
        // contains, so the strip tells a reader where to look and commits to nothing.
        ['Orders', 'Pricing, payment, acceptance'],
        ['Products', 'Information and performance'],
        ['Product use', 'Local laws and safe operation'],
        ['Legal', 'Disclaimers and limitations'],
      ]
    );
    addTermsComponents(body);
    linkPolicyReferences(body, [
      // Longest names first: "Return & Refund Policy" and "Limited Warranty Policy" each
      // contain a shorter policy name that would otherwise capture them.
      { pattern: /(?:KEWELO )?Return & Refund Policy/, href: '/policies/refund-policy' },
      { pattern: /(?:KEWELO )?Limited Warranty Policy/, href: '/pages/warranty-policy' },
      { pattern: /Shipping Policy/, href: '/policies/shipping-policy' },
      { pattern: /Privacy Policy/, href: '/policies/privacy-policy' },
    ]);
  }

  if (kind === 'warranty') {
    addWarrantyComponents(body);
    linkPolicyReferences(body, [
      { pattern: /KEWELO Return and Refund Policy/, href: '/policies/refund-policy' },
    ]);
  }

  // These all run past twenty sections, which makes a jump menu longer than the screen and
  // slower to use than scrolling. They rely on heading hierarchy and spacing instead.
  if (
    kind === 'shipping' ||
    kind === 'refund' ||
    kind === 'warranty' ||
    kind === 'privacy' ||
    kind === 'terms'
  ) {
    return;
  }

  const nav = buildNav(headings);
  // Between the title and the body, so the reading order is title → contents → policy.
  body.before(nav);
  container.classList.add('policy--has-toc');

  // Open as a sidebar on desktop, collapsed as a disclosure below that.
  const desktop = window.matchMedia(DESKTOP);
  const sync = () => {
    nav.toggleAttribute('open', desktop.matches);
  };
  sync();
  desktop.addEventListener('change', sync);

  trackActive(headings, nav);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
